import React, { useEffect, useRef, useState } from 'react'
import { StyledLabel } from '../../assets/styledComponents/Global'
import styled, { useTheme } from 'styled-components';
import StyledButton from '../../app/components/styledComponents/StyledButton';
import CameraIcon from '../../app/components/svgIcons/CameraIcon';
import MicIcon from '../../app/components/svgIcons/MicIcon';
import WifiIcon from '../../app/components/svgIcons/WifiIcon';
// import BrightnessIcon from '../../app/components/svgIcons/BrightnessIcon';
import WavesIcon from '../../app/components/svgIcons/WavesIcon';
import ReloadIcon from '../../app/components/svgIcons/ReloadIcon';
import InfoIcon from '../../app/components/svgIcons/InfoIcon';
import { toast } from 'react-toastify';
import ClockIcon from '../../app/components/svgIcons/ClockIcon';
import { useWindowSize } from '../../utils/helper';
import StyledToolTip from '../../app/components/styledComponents/StyledToolTip';

const GettingReadyAssessment = ({ data, setTimeLeft, setReadyForAssessment, sessionLoading, startSession, token }) => {
    const theme = useTheme();
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const animationFrameRef = useRef(null);
    const dataArrayRef = useRef(null);
    const analyserRef = useRef(null);
    const audioContextRef = useRef(null);
    const [hasPermission, setHasPermission] = useState(false);
    const [firstPermission, setFirstPermission] = useState(true);
    const [barHeights, setBarHeights] = useState([20, 24, 28, 24, 20, 16, 28, 24, 20, 16]);
    const [isActive, setIsActive] = useState(false);

    const [videoDevices, setVideoDevices] = useState([]);
    const [activeDeviceIndex, setActiveDeviceIndex] = useState(0);

    const isSmallScreen = useWindowSize();
    const AudioWaveIcon = () => {
        return (
            <div style={{
                width: '35px',
                height: '35px',
                borderRadius: '50%',
                backgroundColor: '#1d1d1d',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'absolute',
                top: isSmallScreen ? '20px' : '',
                bottom: isSmallScreen ? '' : '25px',
                right: isSmallScreen ? '87%' : '90%',
                zIndex: 99,
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            }}>
                <svg width="20" height="20" viewBox="0 0 40 40" fill="none">
                    {barHeights.map((h, i) => (
                        <rect
                            key={i}
                            x={5 + i * 4}
                            y={29 - h}
                            width="2"
                            height={h}
                            rx="2"
                            fill={"#ffffff"} // Gray when inactive
                            opacity={1}
                        />
                    ))}
                </svg>
            </div>
        );
    };
    const selectedDeviceId = videoDevices[activeDeviceIndex]?.deviceId;

    useEffect(() => {
        navigator.mediaDevices.getUserMedia({
            video: selectedDeviceId ? { deviceId: { exact: selectedDeviceId } } : true, audio: true
        })
            .then(mediaStream => {
                streamRef.current = mediaStream;
                if (videoRef.current) {
                    videoRef.current.srcObject = mediaStream;
                }
                setHasPermission(true);
                audioContextRef.current = new (window.AudioContext ||
                    window.webkitAudioContext)();
                const source = audioContextRef.current.createMediaStreamSource(mediaStream);
                analyserRef.current = audioContextRef.current.createAnalyser();

                analyserRef.current.fftSize = 2048;
                const bufferLength = analyserRef.current.frequencyBinCount;
                dataArrayRef.current = new Uint8Array(bufferLength);

                source.connect(analyserRef.current);
                // drawWaveform();
                drawAudioWave();
            })
            .catch(err => {
                console.error("Permission denied:", err);
                setHasPermission(false);
                setFirstPermission(false);
            });

        return () => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
            }
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
            if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
                audioContextRef.current.close();
            }
        };
    }, [activeDeviceIndex]);
    useEffect(() => {
        if (videoRef.current && streamRef?.current) {
            videoRef.current.srcObject = streamRef?.current;
        }
    }, [streamRef?.current, videoRef]);

    const getReadyPoints = [
        {
            icon: <CameraIcon />,
            text: 'Camera & Lighting',
            subText: 'Sit in front of the camera with your face centred, evenly lit, and easy to see.',
        },
        {
            icon: <MicIcon />,
            text: 'Microphone',
            subText: ' Test your mic by speaking a few words to check your voice is clear.',
        },
        {
            icon: <WifiIcon />,
            text: 'Internet',
            subText: 'Confirm your connection is stable to avoid interruptions during recording.',
        },
        // {
        //     icon: <BrightnessIcon />,
        //     text: 'Adjust Your Lightning',
        //     subText: 'Face a window or use a lamp so your face is well-lit and easy to see.',
        // },
        {
            icon: <WavesIcon />,
            text: 'Environment',
            subText: 'Choose a quiet space, mute notifications, and close unnecessary apps to reduce background noise',
        },
    ];


    useEffect(() => {
        navigator.mediaDevices.enumerateDevices().then(devices => {
            const cams = devices.filter(d => d.kind === "videoinput");
            setVideoDevices(cams);
        });
    }, []);


    const toggleCamera = () => {
        if (videoDevices.length > 1) {
            setActiveDeviceIndex((prev) => (prev + 1) % videoDevices.length);
        }
    };



    function secureRandom(min, max) {
        const array = new Uint32Array(1);
        crypto.getRandomValues(array);
        return min + (array[0] / 0xffffffff) * (max - min);
    }

    const drawAudioWave = () => {
        if (!analyserRef.current || !dataArrayRef.current) return;

        const animate = () => {
            analyserRef.current.getByteFrequencyData(dataArrayRef.current);

            let sum = 0;
            for (let i = 0; i < dataArrayRef.current.length; i++) {
                sum += dataArrayRef.current[i];
            }
            const avg = sum / dataArrayRef.current.length; // 0 to 255

            const threshold = 30; // Adjust as needed

            const intensity = avg / 255;
            const dynamicHeight = 15 + intensity * 20; // 15–35px

            // Check if sound is active
            const isActiveNow = avg > threshold;

            if (isActiveNow && !isActive) {
                setIsActive(true);
            } else if (!isActiveNow && isActive) {
                setIsActive(false);
            }

            // Only update bars if sound is active
            if (isActiveNow) {
                const newHeights = Array(10).fill(dynamicHeight).map(h => h + secureRandom(-2.5, 2.5));
                setBarHeights(newHeights);
            }

            animationFrameRef.current = requestAnimationFrame(animate);
        };

        animate();
    };

    const startGlobalTimer = async () => {
        if (!data?.videoApplication?.allowedTime) {
            // toast.error('Video profile data is missing. Please contact support.');
            setReadyForAssessment(true)
            return;
        }
        let values = {
            token: token,
        };
        try {
            await startSession(values).then((res) => {

                const allowedTime = data?.videoApplication?.allowedTime;
                if (allowedTime) {
                    setTimeLeft(allowedTime * 60); // seconds
                }
                setReadyForAssessment(true)
                // toast.success('Video profile session started successfully.')

            });
        } catch (e) {
            toast.error('An error occurred while starting the video profile.');
            console.error(e);
        }
    }




    return (
        <div>
            {!isSmallScreen && <div className='bg-body p-3 d-flex justify-content-between align-items-center'>
                <StyledLabel size="20px" fontWeight="400" color={theme?.darktext} className="mb-3 font-fredoka fw-normal">
                    {data?.videoApplication?.videoProfile?.title || 'Video Profile Title'}
                </StyledLabel>
                {data?.videoApplication?.allowedTime && <div className='d-flex align-items-center gap-2 cursor-pointer'>
                    <StyledToolTip
                        title={'Overall time allocated for the video interview  '}

                        placement="bottom"
                    >

                        <ClockIcon color={theme?.darktext} size={17} className='me-2' />
                        <StyledLabel size="15px" fontWeight="500" color={theme?.subText} className="" >
                            {data?.videoApplication?.allowedTime ? ` ${data?.videoApplication?.allowedTime} Minutes` : 'No time limit'}</StyledLabel>
                    </StyledToolTip>

                </div>}
            </div>}

            <div className={`d-flex ${isSmallScreen ? 'justify-content-center position-relative p-0 w-100' : 'justify-content-center bg-white'}  flex-column `} style={{ height: `calc(100vh - ${isSmallScreen ? '83px' : '160px'})` }}>
                <div className={`d-flex  ${isSmallScreen ? 'align-items-center mt-2 flex-column gap-2' : ' align-items-center  gap-5'} justify-content-center `} style={{ marginRight: isSmallScreen ? '0' : '120px', marginLeft: isSmallScreen ? '0' : '120px' }} >
                    {!isSmallScreen && <div className='d-flex flex-column me-4'>
                        <StyledLabel size="22px" fontWeight="600" color={theme?.darktext} className="mb-4 ">
                            Before you start let’s Make Sure Everything Works </StyledLabel>
                        {getReadyPoints.map(({ icon, text, subText }, index) => (
                            <div className='d-flex align-items-center mb-4' key={index + text}>
                                <div className='bg-secondary d-flex justify-content-center align-items-center' style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, }}>
                                    {icon}
                                </div>
                                <div className='d-flex flex-column ms-3'>
                                    <StyledLabel size="16px" fontWeight="600" color={theme?.darktext}>{text}</StyledLabel>
                                    <StyledLabel size="14px" fontWeight="500" color={theme?.subText}>{subText}</StyledLabel>
                                </div>
                            </div>
                        ))}
                    </div>}
                    <div className={`bg-white p-2 pb-3 ${isSmallScreen ? 'w-100' : ''}`}>

                        <div className={`${isSmallScreen ? 'justify-content-center' : ' justify-content-center   ms-4 '} d-flex align-items-center rounded position-relative`}
                            style={{ width: '100%', height: '100%', minWidth: isSmallScreen ? "100%" : '600px', maxWidth: isSmallScreen ? "100%" : '600px', maxHeight: isSmallScreen ? "266px" : '390px', minHeight: isSmallScreen ? "266px" : '390px', background: !hasPermission && theme?.darktext, color: !hasPermission && theme?.white }}>
                            {(() => {
                                let previewContent;
                                if (hasPermission) {
                                    previewContent = (
                                        <>
                                            <video ref={videoRef} autoPlay playsInline muted style={{ width: '100%', transform: 'scaleX(-1)', height: '100%', objectFit: 'cover', borderRadius: '8px' }} />
                                            <AudioWaveIcon />
                                            {videoDevices?.length > 1 &&
                                                <div
                                                    role="button"
                                                    tabIndex={0}
                                                    aria-label="Switch Camera"
                                                    style={{
                                                        width: '35px',
                                                        height: '35px',
                                                        color: 'white',
                                                        borderRadius: '50%',
                                                        backgroundColor: '#1d1d1d',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        cursor: 'pointer',
                                                        position: 'absolute',
                                                        top: isSmallScreen ? '20px' : '25px',
                                                        right: isSmallScreen ? '6%' : '4%',
                                                        zIndex: 99
                                                    }}
                                                    onClick={toggleCamera}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault();
                                                            toggleCamera();
                                                        }
                                                    }}
                                                >
                                                    <ReloadIcon />
                                                </div>
                                            }

                                        </>
                                    );
                                } else if (!hasPermission && !firstPermission) {
                                    previewContent = (
                                        <div className={`d-flex flex-column align-items-center justify-content-center px-5 mx-4`} style={{ color: theme?.white }}>
                                            <div className='d-flex align-items-center justify-content-center mb-3'>
                                                <CameraIcon />
                                                <MicIcon className='mx-3' />
                                            </div>
                                            <StyledLabel size="16px" fontWeight="600" color={theme?.white} className="mt-3 mb-2" >Oops!</StyledLabel>
                                            <p className='text-white text-center'>We couldn’t detect your camera or microphone. To join the
                                                assessment please allow access in your browser or device setting to proceed.</p>
                                        </div>
                                    );
                                } else {
                                    previewContent = "";
                                }
                                return previewContent;
                            })()}
                        </div>
                    </div>

                    {isSmallScreen && <div className='d-flex flex-column bg-white w-100 p-2 rounded overflow-y-auto' style={{ height: `calc(100vh - 390px)` }} >
                        <StyledLabel size="22px" fontWeight="600" color={theme?.darktext} className="mb-4 ">
                            Before you start let’s Make Sure Everything Works </StyledLabel>
                        {getReadyPoints.map(({ icon, text, subText }, index) => (
                            <div className='d-flex align-items-center mb-4' key={index + text}>
                                <div className='bg-secondary d-flex justify-content-center align-items-center' style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, }}>
                                    {icon}
                                </div>
                                <div className='d-flex flex-column ms-3'>
                                    <StyledLabel size="16px" fontWeight="600" color={theme?.darktext}>{text}</StyledLabel>
                                    <StyledLabel size="14px" fontWeight="500" color={theme?.subText}>{subText}</StyledLabel>
                                </div>
                            </div>
                        ))}
                    </div>}
                </div>

                <ReadyButtonCotainer isSmallScreen={isSmallScreen} className={isSmallScreen ? 'position-absolute px-3' : 'position-relative'}>

                    <StyledButton variant={isSmallScreen ? 'white' : "primary"} loading={sessionLoading} className={`${isSmallScreen ? 'mt-3' : 'mt-5'} d-flex mx-auto`} disabled={!hasPermission || sessionLoading} onClick={startGlobalTimer} >
                        Im Ready For Assessment
                    </StyledButton>

                    <div className={`d-flex align-items-center text-center justify-content-center gap-2 ${isSmallScreen ? 'mt-1' : 'mt-3'} `}>
                        <InfoIcon /> <StyledLabel weight='500' size={isSmallScreen ? '12px' : '14px'} color={isSmallScreen ? theme?.white : theme?.subText} >  Clicking this button will initiate the video profile timer. </StyledLabel>
                    </div>
                </ReadyButtonCotainer>
            </div>

        </div>
    )
}

export default GettingReadyAssessment


const ReadyButtonCotainer = styled.div`
top: ${({ isSmallScreen }) => isSmallScreen ? '22%' : ''};
width: 80%;
left:50%;
transform:translateX(-50%);
`